import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "oak_session_token";

export function setSessionToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

export function getSessionToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export const api = axios.create({
  baseURL: API,
  // `withCredentials: true` forces the browser into "include" mode, which
  // requires `Access-Control-Allow-Credentials: true` on every response. Our
  // Kubernetes ingress rewrites `Access-Control-Allow-Origin` to `*`, and
  // the combo `ACAO: *` + `ACAC: true` is forbidden by the CORS spec —
  // browsers hard-reject it.
  // We've moved auth to Bearer tokens stored in localStorage, so cross-
  // origin cookies are no longer needed. Leaving this `false` keeps the
  // browser in `same-origin` mode for cookies (still set on same origin)
  // while every request carries the Bearer token via the request
  // interceptor below.
  withCredentials: false,
});

// REQUEST interceptor: always attach `Authorization: Bearer <token>` when
// we have one in localStorage. This is the fallback for browsers that
// don't reliably send `SameSite=None; Secure` cookies (iOS Safari,
// Brave, privacy modes) — without this, the user hits a login loop
// because /auth/me 401s even though we just signed them in.
api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// RESPONSE interceptor: surface the DailyLimitModal on any 402.
// Also purge a stale local token on 401 from auth endpoints so stuck
// users aren't trapped in a half-authenticated state forever.
api.interceptors.response.use(
  (res) => {
    // iter70 — auto-broadcast XP snapshots so the header XPBadge (and
    // LevelUpModal via the badge) refresh after every awarding endpoint
    // without each call site re-wiring it. Several endpoints embed an
    // `xp` payload in their JSON body — game complete, quiz grade,
    // streak ping, path generate. We sniff for it here. The badge
    // ignores payloads that don't look like a full snapshot.
    try {
      const xp = res?.data?.xp;
      if (xp && typeof xp === "object" && typeof xp.level === "number") {
        window.dispatchEvent(new CustomEvent("oak:xp-updated", { detail: xp }));
      }
    } catch {}
    return res;
  },
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (status === 402) {
      try {
        window.dispatchEvent(new CustomEvent("oak:daily-limit"));
      } catch {}
    }
    if (status === 401 && (url.includes("/auth/me") || url.includes("/auth/session"))) {
      setSessionToken(null);
    }
    return Promise.reject(err);
  },
);
